import { useForm, Controller } from "react-hook-form";
import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import { Button } from '../../../../../../components/ui/Button';
import { Dropdown } from 'primereact/dropdown';

import { convertToPx, handleSwitchUnit } from "../../../../../../config/layoutHelper";
import { useCreateAspectRatio } from "../../../../../../queries/layouts";

const AspectRatioController = ({
    setCurrentAspectRatio,
    setAspectRatios,
    aspectRatios,
    currentAspectRatio,
    areas,
    setAreas
}) => {
    const { t } = useTranslation();
    const unit = useSelector((state) => state.layout.unit);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            aspectName: "",
            width: "",
            height: ""
        }
    });

    const handleAspectChangeChange = (e) => {
        if (unit?.value == "px") {

            const areasConvertedToPercentage = handleSwitchUnit(areas, currentAspectRatio);
            const selectedValue = e.target.value;
            const selectedAspect = aspectRatios.find(item => item.id === selectedValue);
            setCurrentAspectRatio(selectedAspect);
            const areasConvertedToPX = convertToPx(areasConvertedToPercentage, selectedAspect);
            setAreas(areasConvertedToPX)

        } else {
            const selectedValue = e.target.value;
            const selectedAspect = aspectRatios.find(item => item.id === selectedValue);
            setCurrentAspectRatio(selectedAspect);
        }
    }
    const { mutate: createAspectRatio, isLoading } = useCreateAspectRatio();
    const handleCreateNewAspect = (data) => {
        const newAspect = {
            name: data.aspectName,
            width: parseInt(data.width, 10),
            height: parseInt(data.height, 10),
        };
        createAspectRatio(newAspect, {
            onSuccess: (newAspectRatio) => {
                setAspectRatios((prevAspectRatios) => [...prevAspectRatios, newAspectRatio]);
                setCurrentAspectRatio(newAspectRatio);
                setShowCreateModal(false);
                reset();
            },
        });

    }


    return (
        <>
            <div className="flex items-center gap-3 w-full">
                <span className="text-sm font-semibold text-gray-500 whitespace-nowrap">{t('layouts.designer_aspect_ratio_label')}</span>
                <div className="flex-1">
                    <Dropdown
                        value={currentAspectRatio?.id || ""}
                        options={aspectRatios}
                        optionLabel="name"
                        optionValue="id"
                        filter
                        className="w-full border-none shadow-none bg-gray-50 focus:bg-white transition-colors"
                        onChange={handleAspectChangeChange}
                        placeholder={t('layouts.designer_select_ratio')}
                        pt={{
                            root: { className: "h-9 items-center rounded-lg border border-gray-200 hover:border-indigo-300 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100" },
                            input: { className: "text-sm font-medium text-gray-700 py-2 pl-3" },
                            trigger: { className: "w-8 text-gray-400" },
                            panel: { className: "text-sm shadow-xl border border-gray-100 rounded-lg mt-1" },
                            item: { className: "text-sm py-2 px-3 hover:bg-indigo-50 hover:text-indigo-700 rounded-md m-1 transition-colors" }
                        }}
                    />
                </div>
                <Button 
                    icon="pi pi-plus" 
                    className="w-9 h-9 min-w-[2.25rem] rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100 hover:bg-indigo-100 transition-colors shadow-sm" 
                    tooltip={t('layouts.designer_new_aspect_ratio')}
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => setShowCreateModal(true)} 
                />
            </div>

            {showCreateModal && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[9999] flex justify-center items-center bg-gray-900/10 backdrop-blur-[1px] transition-all duration-300">
                    <div className="bg-white p-6 rounded-2xl shadow-2xl w-[320px] border border-gray-200 transform scale-100 animate-in fade-in zoom-in duration-200">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-lg font-bold text-gray-800">{t('layouts.designer_new_aspect_ratio')}</h2>
                            <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <i className="pi pi-times"></i>
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit(handleCreateNewAspect)} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 pl-1">{t('layouts.designer_ar_name')}</label>
                                <Controller
                                    name="aspectName"
                                    control={control}
                                    defaultValue=""
                                    rules={{
                                        required: t('layouts.designer_required'),
                                        minLength: { value: 3, message: t('layouts.designer_min_3_chars') },
                                    }}
                                    render={({ field, fieldState }) => (
                                        <div>
                                            <input
                                                {...field}
                                                type="text"
                                                className={`w-full px-3 py-2 bg-gray-50 rounded-lg border ${fieldState.error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'} focus:outline-none focus:ring-4 transition-all text-sm text-gray-700 placeholder-gray-400`}
                                                placeholder="e.g. Ultrawide"
                                            />
                                            {fieldState.error && <p className="text-red-500 text-xs mt-1 ml-1">{fieldState.error.message}</p>}
                                        </div>
                                    )}
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 pl-1">{t('layouts.designer_pos_width')}</label>
                                    <Controller
                                        name="width"
                                        control={control}
                                        defaultValue=""
                                        rules={{ required: "Required", min: 1 }}
                                        render={({ field, fieldState }) => (
                                            <div>
                                                <input
                                                    {...field}
                                                    type="number"
                                                    className={`w-full px-3 py-2 bg-gray-50 rounded-lg border ${fieldState.error ? 'border-red-300' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'} focus:outline-none focus:ring-4 transition-all text-sm font-semibold text-center`}
                                                    placeholder="W"
                                                />
                                            </div>
                                        )}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 pl-1">{t('layouts.designer_pos_height')}</label>
                                    <Controller
                                        name="height"
                                        control={control}
                                        defaultValue=""
                                        rules={{ required: "Required", min: 1 }}
                                        render={({ field, fieldState }) => (
                                            <div>
                                                <input
                                                    {...field}
                                                    type="number"
                                                    className={`w-full px-3 py-2 bg-gray-50 rounded-lg border ${fieldState.error ? 'border-red-300' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'} focus:outline-none focus:ring-4 transition-all text-sm font-semibold text-center`}
                                                    placeholder="H"
                                                />
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="flex gap-2 mt-2 pt-2">
                                <Button
                                    label={t('buttons.cancel')}
                                    type="button"
                                    className="flex-1 py-2 bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:text-gray-900 shadow-sm transition-all rounded-lg text-sm font-semibold"
                                    onClick={() => setShowCreateModal(false)}
                                />
                                <Button
                                    label={t('layouts.designer_create_btn')}
                                    type="submit"
                                    className="flex-1 py-2 bg-indigo-600 border-indigo-600 hover:bg-indigo-700 shadow-md shadow-indigo-200 text-white transition-all rounded-lg text-sm font-bold"
                                />
                            </div>
                        </form>
                    </div>
                </div>,
                document.getElementById('portal-root') || document.body
            )}
        </>
    );
};

export default AspectRatioController;
